<!--
  ~ Copyright (c) 1999 - 2020 Accorto, Inc. - All rights reserved.
  -->

<header class="slds-page-header">
  <div class="slds-page-header__row">
    <div class="slds-page-header__col-title">
      <div class="slds-media">
        <div class="slds-media__figure">
          <span [title]="title"
                class="slds-icon_container">
            <img [alt]="title"
                 height="38px" src="/assets/accorto/project-100.svg" width="38px">
          </span>
        </div>
        <div class="slds-media__body">
          <div class="slds-page-header__name">
            <div class="slds-page-header__name-title">
              <h1>
                <span id="ps-hdr">{{title}}</span>
                <span class="slds-page-header__title" id="ps-name">{{projectName}}</span>
              </h1>
            </div>
            <div class="slds-page-header__name-switcher">
              <div class="slds-dropdown-trigger slds-dropdown-trigger_click">
                <button (click)="onProjectSelectClick()"
                        aria-haspopup="true"
                        class="slds-button slds-button_icon slds-button_icon-small"
                        i18n-title
                        id="ps-select-project" title="Select Project">
                  <svg aria-hidden="true" class="slds-button__icon slds-icon_x-small">
                    <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#down"></use>
                  </svg>
                  <span class="slds-assistive-text" i18n>Select Project</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="slds-page-header__col-actions">
      <div class="slds-page-header__controls">

        <div class="slds-avatar slds-m-right_small">
          <img *ngIf="busy" alt="Loading ..." id="busy" src="/assets/images/busy.svg" title="Loading ...">
        </div>
        <div class="slds-page-header__control slds-show_medium">
          <div class="slds-m-right_small slds-show_inline-block">
            <acc-help-button [title]="title">
              <p>We can plan the project top down or bottom up. Distribute or copy the values down or calculate the total from the
                details.</p>
              <p class="help-p">Enter the Planned Effort in hours.</p>
              <p class="help-p">For the Price, we use the Resource Price by default.</p>
              <ul class="slds-list_dotted">
                <li>For planning purposes, enter the <b>Plan Price</b> per hour to calculate the Planned Revenue.
                  It is basically a placeholder for the Resource Price.
                </li>
                <li>The <b>Unit Price</b> is the negotiated price per hour for this deliverable (overwriting the Resource Price)
                </li>
                <li>The <b>Fixed Amount</b> is the negotiated price for the deliverable.</li>
              </ul>
              <p class="help-p">Similar for the <b>Cost</b> side. We use the cost price of the actual Resource for the actual
                information.</p>
              <ul class="slds-list_dotted">
                <li>For planning purposes, enter the <b>Plan Cost</b> per hour to calculate the planned Costs and Margin. It is the
                  placeholder for the Resource cost price.
                </li>
                <li>The Plan Budget is the equivalent for the Fixed Revenue Amount - it is the upper limit we are planning -
                  regardless of the effort.
                </li>
              </ul>
              <p class="help-p">The planned Margin is calculated based on the entries.</p>
            </acc-help-button>
          </div>
        </div>

        <div *ngIf="project" class="slds-page-header__control slds-show_large">
          <button (click)="doRefresh()"
                  class="slds-button slds-button_icon slds-button_icon-border"
                  id="ps-refresh" title="Refresh">
            <svg aria-hidden="true" class="slds-button__icon">
              <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#refresh"></use>
            </svg>
            <span class="slds-assistive-text" i18n>Refresh</span>
          </button>
          <button (click)="onProjectLineNew()" class="slds-button slds-button_neutral"
                  id="ps-new-pl"
                  title="New Project Line">
            <svg aria-hidden="true" class="slds-button__icon slds-button__icon_left">
              <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#new"></use>
            </svg>
            <span i18n>New Line</span>
          </button>
        </div>

        <div class="slds-page-header__control slds-show_medium">
          <div class="slds-form-element">

            <button (click)="onReset()"
                    class="slds-button slds-button_neutral"
                    id="ps-reset">
              <svg aria-hidden="true" class="slds-button__icon slds-button__icon_left">
                <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#undo"></use>
              </svg>
              <span i18n>Reset</span>
            </button>

            <button (click)="onSave()"
                    [disabled]="isSaveDisabled"
                    class="slds-button slds-button_neutral"
                    id="ps-save">
              <svg aria-hidden="true" class="slds-button__icon slds-button__icon_left">
                <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#connected_apps"></use>
              </svg>
              <span i18n>Save</span>
            </button>
          </div>

        </div>

      </div>
    </div>
  </div><!--header-row-->


  <!-- status -->
  <div class="slds-page-header__row">
    <div class="slds-page-header__col-meta">
      <acc-status-line [error]="error" [message]="message"></acc-status-line>
    </div>
  </div>

</header>


<section class="slds-m-vertical_small">
  <div class="slds-scrollable_x">
    <table [formGroup]="sheet.formGroup" class="slds-table slds-table_bordered">
      <thead>
      <tr class="slds-line-height_reset">
        <th scope="col">
          <div class="slds-truncate"
               i18n-title title="Project Lines">
            <span class="slds-m-right_xx-small" i18n>Project Lines</span>
            <span id="line-count" style="font-weight: normal">({{sheet.rowCount}})</span>
          </div>
        </th>
        <th *ngFor="let cc of sheet.columnsDisplay; trackBy: trackByCol"
            [style.background-color]="cc.hdrColor"
            class="slds-text-align_right"
            scope="col"
            style="min-width: 8.5rem">
          <div [title]="cc.title"
               class="slds-truncate">{{cc.label}}</div>
        </th>
        <th scope="col">
          <div class="slds-truncate slds-assistive-text" i18n title="Delete Line">Delete</div>
        </th>
      </tr>
      </thead>

      <tbody>
      <tr class="slds-hint-parent" id="top"><!-- top row -->
        <th data-label="Project Line" scope="row" style="max-width: 250px">
          <div class="slds-truncate"
               i18n-title title="Project">
            <span i18n>Project</span>
            <span>:</span>
            <i id="project-label">{{projectLabel}}</i>
          </div>
        </th>
        <td *ngFor="let cc of sheet.columnsDisplay; trackBy: trackByCol"
            [attr.data-label]="cc.label" role="gridcell">
          <div [title]="cc.typeTitle + cc.label" class="slds-truncate">
            <div class="slds-form-element__control slds-input-has-icon slds-input-has-icon_left">
              <span class="slds-input__icon slds-text-color_weak">{{cc.typeIcon}}</span>
              <input (change)="sheet.onChangeTop(cc.keyTop(), cc)"
                     [formControlName]="cc.keyTop()"
                     [readOnly]="cc.isReadOnly"
                     [id]="cc.getId('top')"
                     class="slds-input slds-text-align_right"
                     style="padding-left: 4px"
                     type="text"/>
            </div>
          </div>
        </td>
        <td></td>
      </tr>

      <tr class="slds-hint-parent" id="delta"><!-- delta row -->
        <th data-label="Delta" scope="row">
          <div class="slds-truncate" title="Delta">
            Delta
          </div>
        </th>
        <td *ngFor="let cc of sheet.columnsDisplay; trackBy: trackByCol"
            [attr.data-label]="cc.label">
          <div [title]="'Delta: ' + cc.label + ': ' + cc.delta"
               class="slds-truncate">
            <div class="slds-form-element__control slds-input-has-fixed-addon">
              <span *ngIf="!cc.isReadOnly"
                    class="slds-form-element__addon"
                    style="margin: 0">
                <button (click)="sheet.onClick(true, cc)"
                        [disabled]="cc.adjustDisabled"
                        [id]="cc.getId('up')"
                        class="slds-button slds-button_icon slds-button_icon-border"
                        title="Up - Adjust Total">
                  <svg aria-hidden="true" class="slds-button__icon">
                    <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#forward_up"></use>
                  </svg>
                  <span class="slds-assistive-text">Up - Adjust Top</span>
                </button>
                <button (click)="sheet.onClick(false, cc)"
                        [disabled]="cc.adjustDisabled"
                        [id]="cc.getId('down')"
                        class="slds-button slds-button_icon slds-button_icon-border"
                        title="Down - Adjust Details">
                  <svg aria-hidden="true" class="slds-button__icon">
                    <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#arrowdown"></use>
                  </svg>
                  <span class="slds-assistive-text">Down - Adjust Details</span>
                </button>
              </span>
              <input [formControlName]="cc.keyDelta()"
                     [id]="'delta_' + cc.propertyName"
                     class="slds-input slds-text-align_right slds-text-color_error"
                     readonly
                     type="text"/>
            </div>
          </div>
        </td>
        <td></td>
      </tr>
      </tbody>


      <tbody><!-- detail rows -->
      <tr *ngFor="let rr of sheet.rows; trackBy: trackByRow; let i = index"
          [attr.data-id]="rr.projectLineId"
          [id]="'r_' + i"
          class="slds-hint-parent">
        <th data-label="Project Line" scope="row">
          <button (click)="onProjectLineEdit(rr)"
                  [title]="rr.projectLineName"
                  [id]="'r_' + i + '_edit'"
                  class="slds-button slds-button_neutral slds-truncate">
            <svg aria-hidden="true" class="slds-button__icon slds-button__icon_left">
              <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#edit"></use>
            </svg>
            <span class="slds-truncate" style="max-width: 250px">
              {{rr.projectLineName}}
            </span>
          </button>
        </th>

        <td *ngFor="let cc of sheet.columnsDisplay; trackBy: trackByCol"
            [attr.data-label]="cc.label" role="gridcell">
          <div [title]="rr.title(cc) + ': ' + cc.getRecordValueRow(rr)"
               class="slds-truncate1">
            <div class="slds-form-element__control slds-input-has-icon slds-input-has-icon_left">
              <span class="slds-input__icon slds-text-color_weak">{{cc.detailIcon}}</span>
              <input (change)="sheet.onChange(rr.key(cc), cc)"
                     [formControlName]="rr.key(cc)"
                     [readOnly]="cc.isReadOnly"
                     [id]="'r_' + i + '_' + cc.propertyName"
                     class="slds-input slds-text-align_right"
                     style="padding-left: 4px"
                     type="text"/>
            </div>
          </div>
        </td>
        <td role="gridcell">
          <button (click)="onProjectLineDelete(rr)"
                  [id]="'r_' + i + '_delete'"
                  [title]="'Delete ' + rr.projectLineName"
                  class="slds-button slds-button_icon slds-button_icon-border slds-button_icon-x-small">
            <svg aria-hidden="true" class="slds-button__icon slds-button__icon_small">
              <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#delete"></use>
            </svg>
            <span class="slds-assistive-text" i18n>Delete Project Line</span>
          </button>
        </td>
      </tr>
      </tbody>

    </table>
  </div>
</section>

<!-- Project Selection -->
<acc-modal-float (closing)="onProjectSelected($event === true ? undefined : null)"
                 *ngIf="showProjectSelect"
                 title="Select Project">
  <acc-data-record-selector (recordSelected)="onProjectSelected($event)"
                            [columnNames]="projectSelectColumns"
                            [records]="projectRecords">
  </acc-data-record-selector>
</acc-modal-float>

<psa-project-line-editor (editorClosed)="onProjectLineEditComplete()"
                         *ngIf="showProjectLineEditor"
                         [initialTab]="projectLineTab"
                         [recordI]="projectLine">
</psa-project-line-editor>
